/**
 * Лайтбокс просмотра фото (правки Артёма, вкладка «Типы номеров»).
 *
 * Открывает фото на весь экран, перелистывается вперёд/назад: кнопками
 * ‹/›, стрелками клавиатуры (← →), свайпом на мобайле (touch). Закрытие —
 * крестиком, кликом по фону или Esc. Используется для галереи фото типа
 * номера; переиспользуемый (принимает массив URL + стартовый индекс).
 *
 * i18n русский. Мобайл 375: кнопки крупные, свайп. a11y: role="dialog".
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface PhotoLightboxProps {
  /** URL'ы фото по порядку. */
  photos: readonly string[];
  /** Индекс открытого фото. */
  index: number;
  /** Подпись (имя типа номера). */
  title?: string;
  onClose: () => void;
  'data-testid'?: string;
}

const SWIPE_THRESHOLD = 40;

export function PhotoLightbox({
  photos,
  index,
  title,
  onClose,
  'data-testid': testId,
}: PhotoLightboxProps): JSX.Element {
  const [cur, setCur] = useState(index);
  const touchX = useRef<number | null>(null);

  const count = photos.length;
  const go = useCallback(
    (delta: number): void => {
      setCur((prev) => (prev + delta + count) % count);
    },
    [count],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const onTouchStart = (e: React.TouchEvent): void => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent): void => {
    if (touchX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const src = photos[cur];

  return (
    <div
      className="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Просмотр фото'}
      data-testid={testId ?? 'photo-lightbox'}
      onClick={onClose}
    >
      <button
        type="button"
        className="photo-lightbox__close"
        onClick={onClose}
        aria-label="Закрыть"
        data-testid="photo-lightbox-close"
      >
        ×
      </button>

      <div
        className="photo-lightbox__stage"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {count > 1 && (
          <button
            type="button"
            className="photo-lightbox__nav photo-lightbox__nav--prev"
            onClick={() => go(-1)}
            aria-label="Предыдущее фото"
            data-testid="photo-lightbox-prev"
          >
            ‹
          </button>
        )}

        {src !== undefined && (
          <img
            className="photo-lightbox__img"
            src={src}
            alt={title !== undefined ? `${title} — фото ${cur + 1}` : `Фото ${cur + 1}`}
            data-testid="photo-lightbox-img"
          />
        )}

        {count > 1 && (
          <button
            type="button"
            className="photo-lightbox__nav photo-lightbox__nav--next"
            onClick={() => go(1)}
            aria-label="Следующее фото"
            data-testid="photo-lightbox-next"
          >
            ›
          </button>
        )}
      </div>

      {count > 1 && (
        <div className="photo-lightbox__counter" data-testid="photo-lightbox-counter">
          {cur + 1} / {count}
        </div>
      )}
    </div>
  );
}
